import * as Lexxy from "@37signals/lexxy"
import { $createActionTextAttachmentNode } from "@37signals/lexxy"

/**
 * AssetPickerExtension
 *
 * Adds an "Asset Library" button to the Lexxy toolbar. When clicked it opens a
 * <dialog> that fetches the account's image assets from the server and lets the
 * user insert one into the editor as an action-text-attachment node.
 *
 * Activation: the <lexxy-editor> element must have a data-asset-picker-url
 * attribute pointing to the server endpoint (content_select_rich_text_assets_path).
 * This is set by the _blueprint_field_rich_text_form.html.erb partial.
 */
export class AssetPickerExtension extends Lexxy.Extension {
  #button = null
  #dialog = null
  #handleAssetInsert = null

  get enabled() {
    return !!this.editorElement.dataset.assetPickerUrl
  }

  initializeToolbar(toolbar) {
    this.#button = this.#buildButton()
    this.#button.addEventListener("click", () => this.#openPicker())

    // Insert after the image upload button; fall back to appending at the end.
    const imageBtn = toolbar.querySelector("button[name=image]")
    if (imageBtn) {
      imageBtn.insertAdjacentElement("afterend", this.#button)
    } else {
      toolbar.appendChild(this.#button)
    }

    // Listen for asset selection events bubbling from the dialog.
    this.#handleAssetInsert = (event) => this.#insertAsset(event.detail)
    this.editorElement.addEventListener("lexxy:asset-insert", this.#handleAssetInsert)
  }

  dispose() {
    this.#button?.remove()
    this.#button = null

    if (this.#dialog) {
      this.#dialog.remove()
      this.#dialog = null
    }

    if (this.#handleAssetInsert) {
      this.editorElement.removeEventListener("lexxy:asset-insert", this.#handleAssetInsert)
      this.#handleAssetInsert = null
    }
  }

  // ── Private ──────────────────────────────────────────────────────────────

  #buildButton() {
    const btn = document.createElement("button")
    btn.type = "button"
    btn.name = "asset-picker"
    btn.title = "Insert from Asset Library"
    btn.className = "lexxy-editor__toolbar-button"
    // Photo stack icon (inline SVG so we need no extra assets).
    btn.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" width="16" height="16" aria-hidden="true">
        <path fill-rule="evenodd" d="M1 5.25A2.25 2.25 0 0 1 3.25 3h13.5A2.25 2.25 0 0 1 19 5.25v9.5A2.25 2.25 0 0 1 16.75 17H3.25A2.25 2.25 0 0 1 1 14.75v-9.5Zm1.5 5.81v3.69c0 .414.336.75.75.75h13.5a.75.75 0 0 0 .75-.75v-2.69l-2.22-2.219a.75.75 0 0 0-1.06 0l-1.91 1.909-.48-.480a.75.75 0 0 0-1.06 0L6.53 11.06l-4.03-4.03v4.03Zm9.25-6.56a1 1 0 1 0-2 0 1 1 0 0 0 2 0Z" clip-rule="evenodd" />
      </svg>
    `
    return btn
  }

  #openPicker() {
    if (!this.#dialog) {
      this.#dialog = this.#buildDialog()
      document.body.appendChild(this.#dialog)
    }

    // Re-fetch every time so the library reflects newly uploaded assets.
    this.#loadAssets()
    this.#dialog.showModal()
  }

  #buildDialog() {
    const dialog = document.createElement("dialog")
    dialog.className = "asset-picker-dialog"

    // Explicit centering: browser UA dialog centering fights display:flex on the
    // <dialog> element itself, leaving it pinned at x=0. Use transform-based
    // centering instead so the inner flex layout doesn't interfere.
    // z-index must be > 50 (the sidebar's lg:z-50 class) to paint on top.
    dialog.style.cssText = [
      "position: fixed",
      "top: 50%",
      "left: 50%",
      "transform: translate(-50%, -50%)",
      "width: min(720px, 92vw)",
      "border-radius: 0.5rem",
      "border: 1px solid #e7e5e4",
      "padding: 0",
      "box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1)",
      "z-index: 9999",
      "overflow: hidden",
    ].join(";")

    // Inner flex container handles the header + scrollable body layout.
    dialog.innerHTML = `
      <div style="display:flex;flex-direction:column;max-height:80vh;overflow:hidden;">
        <div style="display:flex;align-items:center;justify-content:space-between;padding:0.75rem 1rem;border-bottom:1px solid #e7e5e4;flex-shrink:0;">
          <h2 style="font-size:0.875rem;font-weight:600;color:#1c1917;margin:0;">Asset Library</h2>
          <button type="button" class="asset-picker-close" aria-label="Close"
                  style="color:#78716c;background:none;border:none;cursor:pointer;font-size:1.25rem;line-height:1;padding:0.25rem;">
            &times;
          </button>
        </div>
        <div class="asset-picker-body" style="overflow-y:auto;flex:1;min-height:0;padding:0.75rem;">
          <p style="color:#78716c;font-size:0.875rem;">Loading&hellip;</p>
        </div>
      </div>
    `

    dialog.querySelector(".asset-picker-close").addEventListener("click", () => dialog.close())

    // Clicking the backdrop (outside the dialog box) closes the dialog.
    dialog.addEventListener("click", (event) => {
      if (event.target === dialog) dialog.close()
    })

    // Wire up asset selection after each fetch populates the body.
    dialog.addEventListener("click", (event) => {
      const card = event.target.closest("[data-lexxy-insert-asset]")
      if (!card) return

      const assetData = JSON.parse(card.dataset.lexxyInsertAsset)
      this.editorElement.dispatchEvent(
        new CustomEvent("lexxy:asset-insert", { detail: assetData })
      )
      dialog.close()
    })

    return dialog
  }

  async #loadAssets() {
    const url = this.editorElement.dataset.assetPickerUrl
    const body = this.#dialog.querySelector(".asset-picker-body")
    body.innerHTML = `<p style="color:#78716c;font-size:0.875rem;">Loading&hellip;</p>`

    try {
      const response = await fetch(url, {
        headers: {
          "Accept": "text/html",
          "X-Requested-With": "XMLHttpRequest",
        },
        credentials: "same-origin",
      })

      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      body.innerHTML = await response.text()
    } catch (err) {
      body.innerHTML = `<p style="color:#dc2626;font-size:0.875rem;">Could not load assets: ${err.message}</p>`
    }
  }

  #insertAsset({ sgid, src, contentType, fileName, fileSize, width, height }) {
    this.editorElement.editor.update(() => {
      const node = $createActionTextAttachmentNode({
        sgid,
        src,
        contentType,
        fileName,
        fileSize: fileSize ? Number(fileSize) : undefined,
        width:    width    ? Number(width)    : undefined,
        height:   height   ? Number(height)   : undefined,
        previewable: contentType?.startsWith("image/"),
      })

      this.editorElement.contents.insertAtCursor(node)
    })

    this.editorElement.focus()
  }
}
